// Native
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"

// Components
import { Button, ButtonGroup, Divider, Flex, HStack, ModalBody, ModalFooter, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Form, Icon, Modal } from "components"

// Config
import { API, Mask } from "config"

// Screens
import { Authenticate } from "screens"

export default function Distributor({ data:consultant, onSuccess = () => {}, ...props }){

    const toast = useToast()
    const navigate = useNavigate()
    const [ form, setForm ] = useState({ customer_id: consultant?.customer_id })
    const [ errors, setErrors ] = useState()
    const [ loading, setLoading ] = useState(false)
    const auth = useDisclosure()

    const onChange = ({ target }) => {
        const { name, value } = target
        switch(name){
            case 'doc_cnpj': setForm({...form, [name]: Mask.cnpj(value)}); break;
            case 'dob': setForm({...form, [name]: Mask.date(value)}); break;
            case 'mobile': setForm({...form, [name]: Mask.mobile(value)}); break;
            default: setForm({...form, [name]: value}); break;
        }
    }

    const handleSubmit = async () => {
        setLoading(true)
        setErrors(false)

        try{
            const callback = await API.post('/distributors', {...form, ...{ customer_id: consultant?.customer_id }})
            toast({ title: `Núcleo ${form.title} registrado`, status: 'info' })
            props.onClose()
            onSuccess()
            setForm({})
            navigate(`/distributors/${callback?.data?.id}`)
        }catch(err){
            setErrors(err?.response?.data?.errors)
            toast({ title: 'Não foi possível criar o núcleo.', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    const searchCNPJ = async (doc_cnpj) => {
        setLoading(true)

        try{
            const callback = await API.get(`/ext/cnpj/${doc_cnpj.replace("/", '')}`)

            const company = {
                company_name: callback.data?.['RAZAO SOCIAL'],
                dob: callback.data?.['DATA ABERTURA'],
                address_line_1: callback.data?.['TIPO LOGRADOURO'] + ' ' + callback.data?.['LOGRADOURO'],
                address_complement: callback.data?.['COMPLEMENTO'],
                address_number: callback.data?.['NUMERO'],
                address_neighborhood: callback.data?.['BAIRRO'],
                zip: callback.data?.['CEP'],
                work_number: callback.data?.['DDD'] + callback.data?.['TELEFONE'],
                email: callback.data?.['EMAIL'],
                doc_cnpj
            }

            setForm({...form, ...company })

            toast({ description: 'Dados do CNPJ foram preenchidos automaticamente.', position: 'bottom-right', status: 'success' })
        }catch(err){
            toast({ description: 'Não foi possível localizar informações sobre o CNPJ informado.', position: 'bottom-right' })
        }

        setLoading(false)
    }

    useEffect(() => {
        if(form?.doc_cnpj?.length >= 18){ searchCNPJ(form?.doc_cnpj) }
    }, [form?.doc_cnpj])

    return(
    <>
    <Modal loading={loading} size="lg" {...props}>
        <ModalBody>
            <Stack spacing={6}>
                <Flex alignItems="center" flexDirection="column">
                    <Icon name="distributor" w={50} h={50} />
                    <Text fontSize="lg" children="Criar núcleo de distribuição" mt={2} />
                </Flex>

                <Form.Form onSubmit={auth.onOpen}>
                    <Form.Text
                        isDisabled
                        label="Cliente associado:"
                        value={consultant?.customer?.name}
                        helper="O núcleo será atribuído a este cliente."
                        size="lg"
                    />

                    <Divider />

                    <Form.Text
                        required
                        label="Nome do núcleo:"
                        name="name"
                        value={form?.name}
                        error={errors?.name}
                        onChange={onChange}
                        isDisabled={loading}
                        placeholder={`Ex.: Núcleo ${consultant?.customer?.name}`}
                        helper="Este é o nome público que aparecerá nos escritórios."
                        size="lg"
                        fontWeight="bold"
                    />

                    <Divider />

                    <Form.Text
                        required
                        label="CNPJ:"
                        name="doc_cnpj"
                        value={form?.doc_cnpj}
                        error={errors?.doc_cnpj}
                        onChange={onChange}
                        isDisabled={loading}
                        size="lg"
                        fontWeight="bold"
                    />

                    <Form.Text
                        required
                        label="Razão Social:"
                        name="company_name"
                        value={form?.company_name}
                        error={errors?.company_name}
                        onChange={onChange}
                        isDisabled={loading}
                    />

                    <Form.Text
                        required
                        label="Data de abertura da empresa:"
                        name="dob"
                        value={form?.dob}
                        error={errors?.dob}
                        onChange={onChange}
                        isDisabled={loading}
                    />

                    <Form.Text
                        required
                        label="Inscrição Estadual (IE):"
                        name="doc_ie"
                        value={form?.doc_ie}
                        error={errors?.doc_ie}
                        onChange={onChange}
                        isDisabled={loading}
                        helper="Necessário para gerar notas fiscais."
                    />

                    <Divider />

                    <Form.Text
                        required
                        label="E-mail:"
                        name="email"
                        value={form?.email}
                        error={errors?.email}
                        onChange={onChange}
                        isDisabled={loading}
                        helper="Por onde o núcleo receberá notificações importantes e utilizado para se autenticar."
                    />

                    <Form.Text
                        required
                        label="Celular/Whatsapp:"
                        name="mobile"
                        value={form?.mobile}
                        error={errors?.mobile}
                        onChange={onChange}
                        isDisabled={loading}
                    />

                    <Form.Text
                        required
                        label="Telefone de atendimento:"
                        name="work_number"
                        value={form?.work_number}
                        error={errors?.work_number}
                        onChange={onChange}
                        isDisabled={loading}
                        helper="Este número será exibido publicamente para os consultores."
                    />

                    <Divider />

                    <Form.Loc onChangeCity={city_id => setForm({ ...form, city_id })} isDisabled={loading} />

                    <Form.Text
                        required
                        label="Endereço (Linha I):"
                        name="address_line_1"
                        value={form?.address_line_1}
                        error={errors?.address_line_1}
                        onChange={onChange}
                        isDisabled={loading}
                    />

                    <Form.Text
                        label="Endereço (Linha II):"
                        name="address_line_2"
                        value={form?.address_line_2}
                        error={errors?.address_line_2}
                        onChange={onChange}
                        isDisabled={loading}
                    />

                    <HStack>
                        <Form.Text
                            required
                            label="Bairro/Setor:"
                            name="address_neighborhood"
                            value={form?.address_neighborhood}
                            error={errors?.address_neighborhood}
                            onChange={onChange}
                            isDisabled={loading}
                            size="sm"
                        />

                        <Form.Text
                            required
                            label="Código Postal (CEP):"
                            name="zip"
                            value={form?.zip}
                            error={errors?.zip}
                            onChange={onChange}
                            isDisabled={loading}
                            size="sm"
                        />
                    </HStack>

                    <HStack>
                        <Form.Text
                            required
                            label="Número:"
                            name="address_number"
                            value={form?.address_number}
                            error={errors?.address_number}
                            onChange={onChange}
                            isDisabled={loading}
                            size="sm"
                        />

                        <Form.Text
                            required
                            label="Complemento:"
                            name="address_complement"
                            value={form?.address_complement}
                            error={errors?.address_complement}
                            onChange={onChange}
                            isDisabled={loading}
                            size="sm"
                        />
                    </HStack>

                </Form.Form>
            </Stack>
        </ModalBody>
        <ModalFooter>
            <ButtonGroup size="sm" isDisabled={loading}>
                <Button children="Criar núcleo" leftIcon={<Icon name="distributor" />} onClick={auth.onOpen} />
                <Button colorScheme="red" children="Cancelar" onClick={props.onClose} />
            </ButtonGroup>
        </ModalFooter>
    </Modal>

    <Authenticate {...auth} onSuccess={handleSubmit} />
    </>
    )
}
